import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import KeyboardDoubleArrowLeftRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowLeftRounded";
import KeyboardDoubleArrowRightRoundedIcon from "@mui/icons-material/KeyboardDoubleArrowRightRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";

import {
  AppBar,
  Box,
  Breadcrumbs,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { useTheme } from "@mui/material/styles";
import { useMemo, useState } from "react";
import {
  NavLink,
  Outlet,
  useLocation,
} from "react-router-dom";

import {
  getRouteMeta,
  navigationSections,
} from "../../config/navigation";

import { usePermissions } from "../../hooks/usePermissions";
import { AccountMenu } from "../account/AccountMenu";

const EXPANDED_WIDTH = 236;
const COLLAPSED_WIDTH = 62;

function Sidebar({
  collapsed,
  pathname,
  onNavigate,
}) {
  const { hasPermission } = usePermissions();

  const visibleSections = useMemo(() => {
    return navigationSections
      .map((section) => ({
        ...section,

        items: section.items.filter((item) => {
          if (!item.permission) {
            return true;
          }

          return hasPermission(item.permission);
        }),
      }))
      .filter((section) => section.items.length > 0);
  }, [hasPermission]);

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        minHeight: 0,
        flexDirection: "column",
        bgcolor: "#FFFFFF",
      }}
    >
      <Box
        sx={{
          minHeight: 78,
          px: collapsed ? 1 : 1.4,
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #E3E7EC",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src="/brand/spend2connect-logo.png"
          alt="Spend2Connect"
          sx={{
            width: collapsed ? 42 : 184,
            height: collapsed ? 42 : 60,
            objectFit: "contain",
            objectPosition: "left center",
            transform: collapsed
              ? "scale(1.04)"
              : "scale(1.18)",
            transformOrigin: "left center",
          }}
        />
      </Box>

      <List
        component="nav"
        disablePadding
        sx={{
          flex: 1,
          minHeight: 0,
          py: 1.25,
          overflow: "hidden",
        }}
      >
        {visibleSections.map((section) => (
          <Box
            key={section.heading}
            sx={{ mb: 1.15 }}
          >
            {!collapsed ? (
              <Typography
                sx={{
                  px: 1.45,
                  mb: 0.45,
                  color: "#8792A2",
                  fontSize: "0.58rem",
                  fontWeight: 800,
                  letterSpacing: "0.13em",
                  textTransform: "uppercase",
                }}
              >
                {section.heading}
              </Typography>
            ) : (
              <Divider
                sx={{
                  mx: 0.8,
                  mb: 0.6,
                }}
              />
            )}

            {section.items.map((item) => {
              const active = item.exact
                ? pathname === item.activePrefix
                : pathname.startsWith(
                    item.activePrefix,
                  );

              const button = (
                <ListItemButton
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  onClick={onNavigate}
                  selected={active}
                  sx={{
                    minHeight: 34,
                    px: collapsed ? 0.5 : 1.35,
                    py: 0.25,
                    color: active
                      ? "#2F5BEA"
                      : "#445066",
                    borderLeft: "3px solid",
                    borderLeftColor: active
                      ? "#2F5BEA"
                      : "transparent",

                    "&.Mui-selected": {
                      bgcolor: "#EEF3FF",
                    },

                    "&.Mui-selected:hover": {
                      bgcolor: "#E8EFFF",
                    },

                    "&:hover": {
                      bgcolor: "#F7F9FC",
                    },
                  }}
                >
                  <Typography
                    noWrap
                    sx={{
                      width: "100%",
                      textAlign: collapsed
                        ? "center"
                        : "left",
                      fontSize: collapsed
                        ? "0.58rem"
                        : "0.75rem",
                      fontWeight: active
                        ? 800
                        : 650,
                    }}
                  >
                    {collapsed
                      ? item.label
                          .slice(0, 2)
                          .toUpperCase()
                      : item.label}
                  </Typography>
                </ListItemButton>
              );

              return collapsed ? (
                <Tooltip
                  key={item.to}
                  title={item.label}
                  placement="right"
                  arrow
                >
                  {button}
                </Tooltip>
              ) : (
                button
              );
            })}
          </Box>
        ))}
      </List>

      <Box
        sx={{
          px: 1.45,
          py: 1.2,
          borderTop: "1px solid #E3E7EC",
          flexShrink: 0,
        }}
      >
        {!collapsed ? (
          <>
            <Typography
              sx={{
                color: "#111827",
                fontSize: "0.67rem",
                fontWeight: 800,
              }}
            >
              Spend2Connect
            </Typography>

            <Typography
              sx={{
                mt: 0.18,
                color: "#8A94A3",
                fontSize: "0.58rem",
              }}
            >
              Management portal
            </Typography>
          </>
        ) : null}
      </Box>
    </Box>
  );
}

export function DashboardWrapper() {
  const theme = useTheme();

  const isMobile = useMediaQuery(
    theme.breakpoints.down("md"),
  );

  const [mobileOpen, setMobileOpen] =
    useState(false);

  const [collapsed, setCollapsed] =
    useState(false);

  const location = useLocation();

  const currentRoute = getRouteMeta(
    location.pathname,
  );

  const drawerWidth = collapsed
    ? COLLAPSED_WIDTH
    : EXPANDED_WIDTH;

  const handleSidebarToggle = () => {
    if (isMobile) {
      setMobileOpen(
        (current) => !current,
      );

      return;
    }

    setCollapsed(
      (current) => !current,
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: "#F4F6F8",
      }}
    >
      <Drawer
        variant={
          isMobile
            ? "temporary"
            : "permanent"
        }
        open={
          isMobile
            ? mobileOpen
            : true
        }
        onClose={() =>
          setMobileOpen(false)
        }
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          width: isMobile
            ? 0
            : drawerWidth,

          flexShrink: 0,

          "& .MuiDrawer-paper": {
            width: isMobile
              ? EXPANDED_WIDTH
              : drawerWidth,

            borderRight:
              "1px solid #DDE2E8",

            bgcolor: "#FFFFFF",
            overflow: "hidden",
          },
        }}
      >
        <Sidebar
          collapsed={
            !isMobile && collapsed
          }
          pathname={location.pathname}
          onNavigate={() =>
            setMobileOpen(false)
          }
        />
      </Drawer>

      <Box
        component="section"
        sx={{
          display: "flex",
          minWidth: 0,
          minHeight: "100vh",
          flex: 1,
          flexDirection: "column",
        }}
      >
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            color: "#172033",
            bgcolor: "#FFFFFF",
            borderBottom:
              "1px solid #DDE2E8",
          }}
        >
          <Toolbar
            disableGutters
            sx={{
              minHeight:
                "58px !important",
              px: 1.5,
              gap: 1,
            }}
          >
            <IconButton
              aria-label={
                isMobile
                  ? "Open navigation"
                  : collapsed
                    ? "Expand navigation"
                    : "Collapse navigation"
              }
              onClick={
                handleSidebarToggle
              }
              sx={{
                width: 38,
                height: 38,
                border:
                  "1px solid #D7DDE5",
                borderRadius: 0,
              }}
            >
              {isMobile ? (
                <MenuRoundedIcon
                  sx={{
                    fontSize: 19,
                  }}
                />
              ) : collapsed ? (
                <KeyboardDoubleArrowRightRoundedIcon
                  sx={{
                    fontSize: 18,
                  }}
                />
              ) : (
                <KeyboardDoubleArrowLeftRoundedIcon
                  sx={{
                    fontSize: 18,
                  }}
                />
              )}
            </IconButton>

            <Typography
              sx={{
                fontSize: "0.88rem",
                fontWeight: 800,
              }}
            >
              {currentRoute.title}
            </Typography>

            <Box sx={{ flex: 1 }} />

            <Breadcrumbs
              separator={
                <ChevronRightRoundedIcon
                  sx={{
                    fontSize: 14,
                  }}
                />
              }
              sx={{
                display: {
                  xs: "none",
                  md: "block",
                },
              }}
            >
              {currentRoute.breadcrumbs.map(
                (label, index) => {
                  const current =
                    index ===
                    currentRoute
                      .breadcrumbs
                      .length -
                      1;

                  return (
                    <Typography
                      key={`${label}-${index}`}
                      sx={{
                        color: current
                          ? "#111827"
                          : "#657083",

                        fontSize:
                          "0.72rem",

                        fontWeight:
                          current
                            ? 700
                            : 500,
                      }}
                    >
                      {label}
                    </Typography>
                  );
                },
              )}
            </Breadcrumbs>

            <IconButton
              aria-label="Notifications"
            >
              <NotificationsNoneOutlinedIcon
                sx={{
                  fontSize: 19,
                }}
              />
            </IconButton>

            <AccountMenu />
          </Toolbar>
        </AppBar>

        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}